import { useState } from 'react';
import { C } from '../../theme';
import { SousTitreSection } from './base';
import { styleChamp } from './champs';
import { api } from '../../api/routes';
import { messageDErreur } from '../../api/useDonnees';

/**
 * Mes donnees : les emporter, ou partir (lot F, 2026-08-27).
 *
 * La politique de confidentialite promet ces deux droits depuis la mise en ligne, et
 * aucun n'avait de chemin dans l'application. Un droit qu'on annonce sans l'ouvrir n'est
 * pas un droit.
 */
export function MesDonnees({
  signaler,
  onCompteSupprime,
}: {
  signaler: (message: string) => void;
  onCompteSupprime: () => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <SousTitreSection>Mes données</SousTitreSection>
      <Telechargement signaler={signaler} />
      <SuppressionDuCompte signaler={signaler} onSupprime={onCompteSupprime} />
    </div>
  );
}

/** Un seul fichier, ecrit par le navigateur : rien ne transite par un tiers. */
function Telechargement({ signaler }: { signaler: (message: string) => void }) {
  const [occupe, setOccupe] = useState(false);

  const telecharger = async () => {
    if (occupe) return;
    setOccupe(true);
    try {
      const donnees = await api.exporterMesDonnees();
      const adresse = URL.createObjectURL(
        new Blob([JSON.stringify(donnees, null, 2)], { type: 'application/json' }),
      );
      const lien = document.createElement('a');
      lien.href = adresse;
      lien.download = 'mes-donnees-colette.json';
      lien.click();
      URL.revokeObjectURL(adresse);
      signaler('Tes données sont dans tes téléchargements');
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setOccupe(false);
    }
  };

  return (
    <div
      style={{
        background: C.carte,
        border: `1px solid ${C.bordure}`,
        borderRadius: 14,
        padding: '13px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.encre }}>Mes données</div>
        <div style={{ fontSize: 12, color: C.secondaire, lineHeight: 1.4 }}>
          Un fichier avec tout ce que Colette sait de toi : ta boîte, ton planning, tes
          listes. Ce que tu ne vois pas dans l’application n’y est pas non plus.
        </div>
      </div>
      <button
        type="button"
        className="pressable"
        disabled={occupe}
        aria-label="Télécharger mes données"
        onClick={() => void telecharger()}
        style={{
          border: `1px solid ${C.violetBordure}`,
          background: C.carte,
          color: C.violetTexte,
          borderRadius: 10,
          padding: '7px 12px',
          fontSize: 13,
          fontWeight: 800,
          fontFamily: 'inherit',
          cursor: occupe ? 'default' : 'pointer',
          flexShrink: 0,
        }}
      >
        {occupe ? '…' : 'Télécharger'}
      </button>
    </div>
  );
}

/**
 * Partir pour de bon.
 *
 * Deroulé en deux temps : le bouton ouvre un panneau qui DIT ce qui va disparaitre, ce
 * qui va rester au foyer, et ce que Colette ne peut pas defaire. Le mot de passe est le
 * second geste — un telephone laisse deverrouille ne doit pas suffire.
 */
function SuppressionDuCompte({
  signaler,
  onSupprime,
}: {
  signaler: (message: string) => void;
  onSupprime: () => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [motDePasse, setMotDePasse] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const supprimer = async () => {
    if (envoi || motDePasse === '') return;
    setEnvoi(true);
    try {
      await api.supprimerMonCompte(motDePasse);
      onSupprime();
    } catch (cause) {
      signaler(messageDErreur(cause));
      setEnvoi(false);
    }
  };

  if (!ouvert) {
    return (
      <button
        type="button"
        className="pressable"
        onClick={() => setOuvert(true)}
        style={{
          border: `1px solid ${C.bordure}`,
          background: C.carte,
          color: C.secondaire,
          borderRadius: 14,
          padding: '13px 16px',
          fontSize: 14,
          fontWeight: 700,
          fontFamily: 'inherit',
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        Supprimer mon compte
      </button>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void supprimer();
      }}
      style={{
        background: C.corailFond,
        borderRadius: 14,
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 800, color: C.corailTexte }}>
        Supprimer mon compte
      </div>
      <div style={{ fontSize: 13, color: C.encre, lineHeight: 1.55 }}>
        Ta boîte, tes photos et tout ce que toi seul pouvais voir disparaissent
        définitivement. Ce que tu as mis dans la vie du foyer y reste, sans ton nom. Si tu
        es la dernière personne du foyer, le foyer entier part avec toi.
      </div>
      <div style={{ fontSize: 12.5, color: C.secondaire, lineHeight: 1.5 }}>
        Ce que Colette ne peut pas défaire : les événements déjà écrits dans ton Google
        Agenda restent chez Google. Ils t’appartiennent, à toi de les y retirer.
      </div>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.6, color: C.secondaire }}>
          TON MOT DE PASSE
        </span>
        <input
          type="password"
          value={motDePasse}
          onChange={(e) => setMotDePasse(e.target.value)}
          aria-label="Ton mot de passe"
          autoComplete="current-password"
          autoFocus
          style={styleChamp}
        />
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="submit"
          className="pressable"
          disabled={envoi || motDePasse === ''}
          style={{
            flex: 1,
            height: 44,
            border: 'none',
            borderRadius: 12,
            background: envoi || motDePasse === '' ? C.violetBordure : C.corailTexte,
            color: '#FFFFFF',
            fontSize: 14,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: envoi || motDePasse === '' ? 'default' : 'pointer',
          }}
        >
          {envoi ? 'Suppression…' : 'Supprimer définitivement'}
        </button>
        <button
          type="button"
          className="pressable"
          onClick={() => {
            setMotDePasse('');
            setOuvert(false);
          }}
          style={{
            height: 44,
            padding: '0 16px',
            border: `1px solid ${C.bordure}`,
            borderRadius: 12,
            background: C.carte,
            color: C.secondaire,
            fontSize: 14,
            fontWeight: 700,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Annuler
        </button>
      </div>
    </form>
  );
}
